import { useMemo, useState, useEffect, useCallback } from 'react';
import { api, apiPost, apiPatch, apiDelete } from '../api';
import { useI18n } from '../i18n/context';
import UserRowCard from '../components/users/UserRowCard';
import ListToolbar from '../ui/ListToolbar';
import ModalShell from '../ui/ModalShell';
import { loadState, persistState, resetState } from '../utils/statePersistence';
import '../styles/ui.css';
import './ListPages.css';

export default function Users() {
  const { t, formatDate } = useI18n();
  const defaults = { q: '', orgFilter: '', roleFilter: '', statusFilter: '', sortKey: 'created_desc', page: 1 };
  const { state: persisted } = loadState('sa-users-state', defaults, {
    q: { param: 'search' },
    orgFilter: { param: 'org' },
    roleFilter: { param: 'role' },
    statusFilter: { param: 'status' },
    sortKey: { param: 'sort' },
    page: { param: 'page', parse: (v) => Math.max(1, parseInt(v, 10) || 1), serialize: (v) => String(v) }
  });

  const [data, setData] = useState({ data: [], pagination: { page: persisted.page, limit: 500, total: 0 } });
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(persisted.page);
  const [q, setQ] = useState(persisted.q);
  const [orgFilter, setOrgFilter] = useState(persisted.orgFilter);
  const [roleFilter, setRoleFilter] = useState(persisted.roleFilter);
  const [statusFilter, setStatusFilter] = useState(persisted.statusFilter);
  const [sortKey, setSortKey] = useState(persisted.sortKey);
  const [toast, setToast] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'employee', organizationId: '' });
  const [creatingOrg, setCreatingOrg] = useState(false);
  const [newOrgName, setNewOrgName] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState('');
  const [showIds, setShowIds] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwdConfirm, setShowPwdConfirm] = useState(false);
  const [showResetPwd, setShowResetPwd] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [createError, setCreateError] = useState('');

  const showToast = (message, isError = false) => {
    setToast({ message, isError });
    setTimeout(() => setToast(null), 4000);
  };

  const loadOrgs = useCallback(() => {
    api('/superadmin/organizations?limit=500')
      .then((r) => {
        const organizations = [...(r.data || [])];
        organizations.sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
        setOrgs(organizations);
      })
      .catch(() => setOrgs([]));
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: 500 });
    if (q) params.set('q', q);
    if (orgFilter) params.set('organizationId', orgFilter);
    if (roleFilter) params.set('role', roleFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (sortKey) params.set('sort', sortKey);
    api(`/superadmin/users?${params}`)
      .then((r) => {
        const items = r?.data || [];
        if (page > 1 && Array.isArray(items) && items.length === 0) {
          setPage(1);
          return;
        }
        setData(r);
      })
      .catch(() => setData({ data: [], pagination: { page: 1, limit: 500, total: 0 } }))
      .finally(() => setLoading(false));
  }, [page, q, orgFilter, roleFilter, statusFilter, sortKey]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    persistState(
      'sa-users-state',
      { q, orgFilter, roleFilter, statusFilter, sortKey, page },
      defaults,
      {
        q: { param: 'search' },
        orgFilter: { param: 'org' },
        roleFilter: { param: 'role' },
        statusFilter: { param: 'status' },
        sortKey: { param: 'sort' },
        page: { param: 'page', serialize: (v) => String(v) }
      }
    );
  }, [q, orgFilter, roleFilter, statusFilter, sortKey, page]);

  const handleCreate = () => {
    setForm({ name: '', email: '', password: '', confirmPassword: '', role: 'employee', organizationId: orgs[0]?.id || '' });
    setCreateError('');
    setShowPwd(false);
    setShowPwdConfirm(false);
    setCreatingOrg(false);
    setNewOrgName('');
    setModal('create');
  };

  const handleEdit = (row) => {
    setForm({ name: row.name, email: row.email, role: row.role });
    setModal({ type: 'edit', id: row.id });
  };

  const handleSaveCreate = async () => {
    setCreateError('');
    if (!form.name.trim() || !form.email.trim()) { showToast(t('users.name') + ' / ' + t('users.email') + ' required', true); return; }
    if (!form.password || form.password.length < 6) { showToast(t('users.newPasswordPlaceholder'), true); return; }
    if (form.password !== form.confirmPassword) { showToast(t('users.passwordMismatch'), true); return; }
    if (!form.organizationId) { showToast(t('users.selectOrg'), true); return; }
    setSaving(true);
    try {
      let organizationId = form.organizationId;
      // Crear nueva organización si se eligió "crear"
      if (organizationId === '__create__') {
        if (!newOrgName.trim()) {
          showToast(t('org.nameRequired') || 'Organization name required', true);
          setSaving(false);
          return;
        }
        const orgResp = await apiPost('/superadmin/organizations', { name: newOrgName.trim(), billing_tier: 'enterprise' });
        organizationId = orgResp.id;
        setOrgs((prev) => [{ id: orgResp.id, name: orgResp.name }, ...prev]);
      }
      await apiPost('/superadmin/users', {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        password: form.password,
        role: form.role,
        organizationId
      });
      showToast(t('toast.created'));
      setModal(null);
      setForm({ name: '', email: '', password: '', confirmPassword: '', role: 'employee', organizationId: orgs[0]?.id || '' });
      setNewOrgName('');
      setCreatingOrg(false);
      setShowPwd(false);
      setShowPwdConfirm(false);
      load();
    } catch (e) {
      const rawMessage = String(e?.message || '');
      const duplicateEmail = e?.status === 409 || /ya existe un usuario con ese email|email.*exist/i.test(rawMessage);
      const message = duplicateEmail ? 'Ya existe un usuario con ese email.' : (rawMessage || t('toast.error'));
      setCreateError(message);
      showToast(message, true);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async () => {
    if (!form.name.trim()) { showToast(t('users.name') + ' required', true); return; }
    const id = modal?.id;
    if (!id) return;
    setSaving(true);
    try {
      await apiPatch(`/superadmin/users/${id}`, { name: form.name.trim(), role: form.role });
      showToast(t('toast.updated'));
      setModal(null);
      load();
    } catch (e) {
      showToast(e.message || t('toast.error'), true);
    } finally {
      setSaving(false);
    }
  };

  const handleStatus = async (row) => {
    setModal({ type: 'status', id: row.id, name: row.name, active: row.isActive });
  };

  const handleConfirmStatus = async () => {
    const id = modal?.id;
    if (!id) return;
    try {
      await apiPatch(`/superadmin/users/${id}/status`, { active: !modal.active });
      showToast(t('toast.updated'));
      setModal(null);
      load();
    } catch (e) {
      showToast(e.message || t('toast.error'), true);
    }
  };

  const handleResetPassword = (row) => {
    setForm({ newPassword: '', confirmNewPassword: '' });
    setShowResetPwd(false);
    setShowResetConfirm(false);
    setModal({ type: 'resetPassword', id: row.id, name: row.name });
  };

  const handleSaveResetPassword = async () => {
    const id = modal?.id;
    if (!id || !form.newPassword || form.newPassword.length < 6) {
      showToast(t('users.newPasswordPlaceholder'), true);
      return;
    }
    if (form.newPassword !== form.confirmNewPassword) {
      showToast(t('users.passwordMismatch'), true);
      return;
    }
    setSaving(true);
    try {
      await apiPost(`/superadmin/users/${id}/reset-password`, { newPassword: form.newPassword });
      showToast(t('toast.passwordReset'));
      setModal(null);
      setForm({ newPassword: '', confirmNewPassword: '' });
      setShowResetPwd(false);
      setShowResetConfirm(false);
    } catch (e) {
      showToast(e.message || t('toast.error'), true);
    } finally {
      setSaving(false);
    }
  };

  const handleMoveOrg = (row) => {
    setForm({ organizationId: row.organizationId || '' });
    setModal({ type: 'move', id: row.id, name: row.name });
  };

  const handleSaveMove = async () => {
    const id = modal?.id;
    if (!id || !form.organizationId) { showToast(t('users.selectOrg'), true); return; }
    setSaving(true);
    try {
      await apiPatch(`/superadmin/users/${id}/move`, { organizationId: form.organizationId });
      showToast(t('toast.updated'));
      setModal(null);
      load();
    } catch (e) {
      showToast(e.message || t('toast.error'), true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteClick = (row) => {
    setDeleteConfirmText('');
    setDeleteError('');
    setModal({ type: 'delete', id: row.id, name: row.name, email: row.email });
  };

  const handleConfirmDelete = async () => {
    const id = modal?.id;
    const email = modal?.email;
    if (!id || saving) return;
    setSaving(true);
    setDeleteError('');
    // cerrar modal inmediatamente y actualizar lista optimista
    setModal(null);
    setData((prev) => {
      const current = prev?.data || [];
      return { ...prev, data: current.filter((u) => u.id !== id) };
    });
    try {
      const resp = await apiDelete(`/superadmin/users/${id}`);
      showToast(t('toast.deleted'));
      if (resp?.warnings && resp.warnings.length) {
        showToast(`Eliminado con avisos: ${resp.warnings.join(', ')}`, true);
      }
      // Refetch en background para asegurar consistencia
      load();
    } catch (e) {
      // tratar 404/410 como éxito idempotente
      if (e?.status === 404 || e?.status === 410) {
        showToast(t('toast.deleted'));
      } else {
        showToast(e?.message || t('toast.error'), true);
        load();
      }
    } finally {
      setSaving(false);
    }
  };

  const { data: list } = data;
  const totalPages = 1;

  const filteredSorted = useMemo(() => {
    let arr = list || [];
    if (q) {
      const term = q.toLowerCase();
      arr = arr.filter((u) =>
        (u.name || '').toLowerCase().includes(term) ||
        (u.email || '').toLowerCase().includes(term)
      );
    }
    if (orgFilter) {
      arr = arr.filter((u) => u.organizationId === orgFilter || u.organizationName === orgFilter);
    }
    if (roleFilter) {
      arr = arr.filter((u) => (u.role || '') === roleFilter);
    }
    if (statusFilter) {
      const isActive = statusFilter === 'active';
      arr = arr.filter((u) => !!u.isActive === isActive);
    }
    const sorter = {
      created_desc: (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
      created_asc: (a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0),
      name_asc: (a, b) => (a.name || '').localeCompare(b.name || ''),
      name_desc: (a, b) => (b.name || '').localeCompare(a.name || ''),
      role: (a, b) => (a.role || '').localeCompare(b.role || ''),
      org: (a, b) => (a.organizationName || '').localeCompare(b.organizationName || '')
    };
    arr = [...arr].sort(sorter[sortKey] || sorter.created_desc);
    return arr;
  }, [list, q, orgFilter, roleFilter, statusFilter, sortKey]);
  function truncateId(id) {
    if (!id) return '—';
    return id.length > 16 ? `${String(id).slice(0, 8)}…${String(id).slice(-4)}` : id;
  }

  function copyId(id) {
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => showToast(t('common.copied'))).catch(() => showToast(t('common.error'), true));
  }

  return (
    <div className="sa-page-frame">
      <div className="sa-page-header">
        <h1 className="sa-page-title">{t('users.title')}</h1>
        <p className="sa-page-subtitle">{t('users.subtitle')}</p>
      </div>
      <div className="ui-page-toolbar">
        <ListToolbar
          title={null}
          subtitle={null}
          searchValue={q}
          onSearchChange={(v) => { setQ(v); setPage(1); }}
          searchPlaceholder={t('users.searchPlaceholder')}
          filters={[
            <select
              key="org-filter"
              className="ui-input"
              style={{ minWidth: '150px' }}
              value={orgFilter}
              onChange={(e) => { setOrgFilter(e.target.value); setPage(1); }}
            >
              <option value="">{t('users.filterOrgAll')}</option>
              {orgs.map((o) => {
                const suffix = o.admin_email ? ` · ${o.admin_email}` : ` · ${String(o.id || '').slice(0, 8)}`;
                return <option key={o.id} value={o.id}>{o.name}{suffix}</option>;
              })}
            </select>,
            <select
              key="role-filter"
              className="ui-input"
              style={{ minWidth: '140px' }}
              value={roleFilter}
              onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
            >
              <option value="">{t('users.filterRolesAll')}</option>
              <option value="employee">{t('users.roleEmployee')}</option>
              <option value="supervisor">{t('users.roleSupervisor')}</option>
              <option value="admin">{t('users.roleAdmin')}</option>
              <option value="superadmin">{t('users.roleSuperadmin')}</option>
            </select>,
            <select
              key="status-filter"
              className="ui-input"
              style={{ minWidth: '130px' }}
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            >
              <option value="">{t('users.filterStatesAll')}</option>
              <option value="active">{t('users.active')}</option>
              <option value="inactive">{t('users.inactive')}</option>
            </select>,
            <select
              key="sort"
              className="ui-input"
              style={{ minWidth: '150px' }}
              value={sortKey}
              onChange={(e) => { setSortKey(e.target.value); setPage(1); }}
            >
              <option value="created_desc">{t('users.sortNewest')}</option>
              <option value="created_asc">{t('users.sortOldest')}</option>
              <option value="name_asc">{t('users.sortNameAsc')}</option>
              <option value="name_desc">{t('users.sortNameDesc')}</option>
              <option value="role">{t('users.sortRole')}</option>
              <option value="org">{t('users.sortOrg')}</option>
            </select>,
            <button
              key="reset"
              type="button"
              className="ui-button ghost"
              onClick={() => {
                const next = resetState('sa-users-state', defaults);
                setQ(next.q); setOrgFilter(next.orgFilter); setRoleFilter(next.roleFilter); setStatusFilter(next.statusFilter); setSortKey(next.sortKey); setPage(next.page);
              }}
            >
              {t('common.reset')}
            </button>
          ]}
          actions={(
            <button type="button" className="ui-button primary" onClick={handleCreate}>
              {t('users.create')}
            </button>
          )}
        />
        {toast && (
          <div className={`sa-toast ${toast.isError ? 'sa-toast--error' : 'sa-toast--success'}`} role="alert">
            {toast.message}
          </div>
        )}
      </div>

      <div className="ui-page-content">
          <div className="ui-list ui-list-scroll">
            {loading ? (
              <div className="ui-empty">{t('common.loading')}</div>
            ) : filteredSorted.length === 0 ? (
              <div className="ui-empty">{t('users.emptyFiltered')}</div>
          ) : (
            filteredSorted.map((row) => (
              <UserRowCard
                key={row.id}
                user={row}
                t={t}
                onAction={(key) => {
                  if (key === 'viewDetails') return handleEdit(row);
                  if (key === 'changeRole') return handleEdit(row);
                  if (key === 'resetPassword') return handleResetPassword(row);
                  if (key === 'toggleStatus') return handleStatus(row);
                  if (key === 'delete') return handleDeleteClick(row);
                }}
              />
            ))
          )}
        </div>
      </div>

      {/* Modal Create */}
      {modal === 'create' && (
        <ModalShell
          title={t('users.create')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button type="button" className="ui-button primary" onClick={handleSaveCreate} disabled={saving}>{saving ? t('common.loading') : t('actions.save')}</button>
            </>
          )}
        >
          {createError && (
            <div className="sa-toast sa-toast--error" role="alert" style={{ position: 'relative', marginBottom: '0.75rem' }}>
              {createError}
            </div>
          )}
          <div className="ui-field">
            <label className="ui-label">{t('users.name')}</label>
            <input type="text" className="ui-input" value={form.name} autoComplete="off" onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.email')}</label>
            <input type="email" className="ui-input" value={form.email} autoComplete="off" name="new-user-email" onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.newPassword')}</label>
            <div className="sa-password-input">
              <input
                type={showPwd ? 'text' : 'password'}
                className="sa-input"
                value={form.password}
                autoComplete="new-password"
                name="new-user-password"
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={t('users.newPasswordPlaceholder')}
              />
              <button type="button" className="sa-password-toggle" onClick={() => setShowPwd((v) => !v)}>
                {showPwd ? t('login.hide') : t('login.show')}
              </button>
            </div>
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.confirmPassword')}</label>
            <div className="sa-password-input">
              <input
                type={showPwdConfirm ? 'text' : 'password'}
                className="sa-input"
                value={form.confirmPassword || ''}
                autoComplete="new-password"
                name="new-user-password-confirmation"
                onChange={(e) => setForm((f) => ({ ...f, confirmPassword: e.target.value }))}
                placeholder={t('users.confirmPasswordPlaceholder')}
              />
              <button type="button" className="sa-password-toggle" onClick={() => setShowPwdConfirm((v) => !v)}>
                {showPwdConfirm ? t('login.hide') : t('login.show')}
              </button>
            </div>
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.role')}</label>
            <select className="ui-input" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="employee">{t('users.roleEmployee')}</option>
              <option value="admin">{t('users.roleAdmin')}</option>
              <option value="supervisor">{t('users.roleSupervisor')}</option>
            </select>
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.organization')}</label>
            <select
              className="ui-input"
              value={creatingOrg ? '__create__' : form.organizationId}
              onChange={(e) => {
                const val = e.target.value;
                if (val === '__create__') {
                  setCreatingOrg(true);
                  setForm((f) => ({ ...f, organizationId: '__create__' }));
                } else {
                  setCreatingOrg(false);
                  setNewOrgName('');
                  setForm((f) => ({ ...f, organizationId: val }));
                }
              }}
              disabled={saving}
            >
              <option value="">{t('users.selectOrg')}</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              <option value="__create__">+ {t('org.createNew') || 'Create new organization…'}</option>
            </select>
            {creatingOrg && (
              <div style={{ marginTop: 8 }}>
                <input
                  className="sa-input"
                  placeholder={t('org.name') || 'Organization name'}
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                />
              </div>
            )}
          </div>
        </ModalShell>
      )}

      {/* Modal Edit */}
      {modal?.type === 'edit' && (
        <ModalShell
          title={t('users.edit')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button type="button" className="ui-button primary" onClick={handleSaveEdit} disabled={saving}>{saving ? t('common.loading') : t('actions.save')}</button>
            </>
          )}
        >
          {modal.id && (
            <div className="ui-subtext" style={{ marginBottom: 8 }}>
              {t('users.userId')}: {modal.id}
            </div>
          )}
          <div className="ui-field">
            <label className="ui-label">{t('users.name')}</label>
            <input type="text" className="ui-input" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.role')}</label>
            <select className="ui-input" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="employee">{t('users.roleEmployee')}</option>
              <option value="admin">{t('users.roleAdmin')}</option>
              <option value="supervisor">{t('users.roleSupervisor')}</option>
            </select>
          </div>
        </ModalShell>
      )}

      {/* Modal Reset password */}
      {modal?.type === 'resetPassword' && (
        <ModalShell
          title={t('users.resetPassword')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button type="button" className="ui-button primary" onClick={handleSaveResetPassword} disabled={saving}>{saving ? t('common.loading') : t('actions.save')}</button>
            </>
          )}
        >
          <p className="ui-subtext">{modal.name}</p>
          <div className="ui-field">
            <label className="ui-label">{t('users.newPassword')}</label>
            <div className="sa-password-input">
              <input
                type={showResetPwd ? 'text' : 'password'}
                className="sa-input"
                value={form.newPassword || ''}
                onChange={(e) => setForm((f) => ({ ...f, newPassword: e.target.value }))}
                placeholder={t('users.newPasswordPlaceholder')}
              />
              <button type="button" className="sa-password-toggle" onClick={() => setShowResetPwd((v) => !v)}>
                {showResetPwd ? t('login.hide') : t('login.show')}
              </button>
            </div>
          </div>
          <div className="ui-field">
            <label className="ui-label">{t('users.confirmPassword')}</label>
            <div className="sa-password-input">
              <input
                type={showResetConfirm ? 'text' : 'password'}
                className="sa-input"
                value={form.confirmNewPassword || ''}
                onChange={(e) => setForm((f) => ({ ...f, confirmNewPassword: e.target.value }))}
                placeholder={t('users.confirmPasswordPlaceholder')}
              />
              <button type="button" className="sa-password-toggle" onClick={() => setShowResetConfirm((v) => !v)}>
                {showResetConfirm ? t('login.hide') : t('login.show')}
              </button>
            </div>
          </div>
        </ModalShell>
      )}

      {/* Modal Move org */}
      {modal?.type === 'move' && (
        <ModalShell
          title={t('users.moveOrg')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button type="button" className="ui-button primary" onClick={handleSaveMove} disabled={saving}>{saving ? t('common.loading') : t('actions.save')}</button>
            </>
          )}
        >
          {modal.id && (
            <div className="ui-subtext" style={{ marginBottom: 6 }}>
              {t('users.userId')}: {modal.id}
            </div>
          )}
          <p className="ui-subtext">{modal.name}</p>
          <div className="ui-field">
            <label className="ui-label">{t('users.organization')}</label>
            <select className="ui-input" value={form.organizationId} onChange={(e) => setForm((f) => ({ ...f, organizationId: e.target.value }))}>
              <option value="">{t('users.selectOrg')}</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        </ModalShell>
      )}

      {/* Modal Status */}
      {modal?.type === 'status' && (
        <ModalShell
          title={modal.active ? t('users.deactivate') : t('users.activate')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button
                type="button"
                className={modal.active ? 'ui-button danger' : 'ui-button primary'}
                onClick={handleConfirmStatus}
                disabled={saving}
                style={{ background: modal.active ? '#ef4444' : undefined, color: '#fff' }}
              >
                {saving ? t('common.loading') : (modal.active ? t('users.deactivate') : t('users.activate'))}
              </button>
            </>
          )}
        >
          <p className="ui-subtext">
            {modal.active ? t('users.confirmDeactivate') : t('users.confirmActivate')}
          </p>
        </ModalShell>
      )}

      {/* Modal Delete */}
      {modal?.type === 'delete' && (
        <ModalShell
          title={t('users.delete')}
          onClose={() => !saving && setModal(null)}
          footer={(
            <>
              <button type="button" className="ui-button ghost" onClick={() => setModal(null)} disabled={saving}>{t('actions.cancel')}</button>
              <button
                type="button"
                className="ui-button primary"
                style={{ background: '#ef4444', color: '#fff' }}
                onClick={handleConfirmDelete}
                disabled={
                  saving ||
                  !modal?.email ||
                  deleteConfirmText.trim().toLowerCase() !== modal.email.trim().toLowerCase()
                }
              >
                {saving ? (t('users.deleting') || t('common.loading')) : t('actions.delete')}
              </button>
            </>
          )}
        >
          <p className="ui-subtext" style={{ marginBottom: 8 }}>
            {t('users.deleteImpact') || 'Se cerrarán sesiones activas y no podrá iniciar sesión. Esta acción no se puede deshacer en la UI.'}
          </p>
          <p className="ui-subtext" style={{ marginBottom: 4 }}>
            {t('users.confirmDeleteStrong') || 'Escribe el email del usuario para habilitar Eliminar:'}
          </p>
          <input
            type="text"
            className="sa-input"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder={modal?.email || ''}
            disabled={saving}
            style={{ width: '100%' }}
          />
          <div className="ui-subtext" style={{ marginTop: 6, color: '#1e293b' }}>
            {modal?.email}
          </div>
          {deleteError && (
            <div className="sa-toast sa-toast--error" role="alert" style={{ position: 'relative', marginTop: '0.65rem' }}>
              {deleteError}
            </div>
          )}
        </ModalShell>
      )}
    </div>
  );
}
